import { useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Cloud, Network } from 'lucide-react';
import { config } from '../config';
import { useAuth } from '../hooks/useAuth';
import { useActiveContextReady } from '../hooks/useActiveContextReady';
import { usePlacements, type Placement } from '../hooks/usePlacements';
import { useCreateCredential, type Credential } from '../hooks/useCredentials';
import CredentialSelect from './CredentialSelect';
import { UniFiKeyPanel } from './UniFiKeyPanel';
import {
  cloudConsoleUrl,
  consoleIntegrationsUrl,
  useAdoptSite,
  useControllers,
  useMatchSite,
  type ConsoleConnection,
  type Controller,
  type DiscoveredSite,
} from '../hooks/useControllers';
import { apiErrorMessage } from '@/utils/apiError';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { hostForUrl } from '@/utils/netValidation';

const backendApi = config.backendApi;
const NEW_KEY = '__new_key__';
const API_KEY_TYPES: readonly Credential['type'][] = ['apiKey'];

interface GatewayStatus {
  id: string;
  status: string;
  regionId: string;
}

/** Online gateways per region, so the sheet can say when a region has none. */
function useGatewayStatus(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<GatewayStatus[]>({
    queryKey: ['gateway-status', organizationId],
    queryFn: async () => {
      const response = await axios.get<{ gateways?: GatewayStatus[] }>(
        `${backendApi}/api/gateways`,
        {
          withCredentials: true,
        },
      );
      return Array.isArray(response.data.gateways) ? response.data.gateways : [];
    },
    enabled: !!organizationId && contextReady,
  });
}

function siteLabel(site: DiscoveredSite): string {
  return site.consoleName ?? site.siteName ?? site.siteReference;
}

/** Words of a name, for suggesting where a console most likely belongs. */
function words(value: string): Set<string> {
  return new Set(
    value
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(
        (w) =>
          w.length > 2 &&
          !['udm', 'ucg', 'pro', 'max', 'unifi', 'console', 'default', 'site'].includes(w),
      ),
  );
}

interface PlacementOption {
  placement: Placement;
  suggested: boolean;
}

/** Every place a console can belong to, best name match first. */
function usePlacementOptions(site: DiscoveredSite | null): PlacementOption[] {
  const { placements } = usePlacements();
  return useMemo(() => {
    const siteWords = site
      ? words(`${site.consoleName ?? ''} ${site.siteName ?? ''}`)
      : new Set<string>();
    return placements
      .map((placement) => ({
        placement,
        suggested: [...words(placement.path)].some((w) => siteWords.has(w)),
      }))
      .sort(
        (a, b) =>
          Number(b.suggested) - Number(a.suggested) ||
          a.placement.path.localeCompare(b.placement.path),
      );
  }, [placements, site]);
}

/** The "Belongs to" picker: regions grouped by division, customers tagged. */
function BelongsToSelect({
  id,
  value,
  onChange,
  options,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: PlacementOption[];
}) {
  const suggested = options.filter((o) => o.suggested);
  const groups = new Map<string, PlacementOption[]>();
  for (const o of options.filter((o) => !o.suggested)) {
    const key = o.placement.divisionName ?? '';
    groups.set(key, [...(groups.get(key) ?? []), o]);
  }
  const item = (o: PlacementOption, withPath: boolean) => (
    <SelectItem key={o.placement.regionId} value={o.placement.regionId}>
      {withPath ? o.placement.path : o.placement.regionName}
      {o.placement.isCustomer && withPath ? '  · Customer' : ''}
    </SelectItem>
  );
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id}>
        <SelectValue placeholder="Choose where this console belongs" />
      </SelectTrigger>
      <SelectContent>
        {suggested.length ? (
          <SelectGroup>
            <SelectLabel>Suggested</SelectLabel>
            {suggested.map((o) => item(o, true))}
          </SelectGroup>
        ) : null}
        {[...groups.entries()]
          .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
          .map(([division, list]) => (
            <SelectGroup key={division || '__none__'}>
              <SelectLabel>
                {division || 'No division'}
                {list[0]?.placement.isCustomer ? ' · Customer' : ''}
              </SelectLabel>
              {list.map((o) => item(o, false))}
            </SelectGroup>
          ))}
      </SelectContent>
    </Select>
  );
}

/** Choose (or clear) where a discovered site belongs. */
export function MatchSiteSheet({
  parent,
  site,
  onClose,
}: {
  parent: Controller;
  site: DiscoveredSite | null;
  onClose: () => void;
}) {
  const options = usePlacementOptions(site);
  const match = useMatchSite(parent.id);
  const [regionId, setRegionId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever another site is opened. `undefined` to start, so a site passed
  // at mount seeds too.
  const [formFor, setFormFor] = useState<DiscoveredSite | null | undefined>(undefined);
  if (site !== formFor) {
    setFormFor(site);
    setRegionId(site?.regionId ?? '');
    setError(null);
  }

  const save = (value: string | null): void => {
    if (!site) return;
    match.mutate(
      { siteId: site.id, regionId: value },
      {
        onSuccess: onClose,
        onError: (e) => {
          setError(apiErrorMessage(e, 'Could not save where this site belongs.'));
        },
      },
    );
  };

  return (
    <AppSheet
      open={site !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={site ? `Where does ${siteLabel(site)} belong?` : 'Belongs to'}
      size="md"
    >
      <AppSheet.Body>
        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="match-region">Belongs to</Label>
          <BelongsToSelect
            id="match-region"
            value={regionId}
            onChange={setRegionId}
            options={options}
          />
          <p className="text-xs text-muted-foreground">
            Its gateways will poll this console once it is adopted, and its devices appear there.
          </p>
        </div>
      </AppSheet.Body>
      <AppSheet.Footer>
        {site?.regionId ? (
          <Button
            variant="outline"
            type="button"
            disabled={match.isPending}
            onClick={() => {
              save(null);
            }}
          >
            Clear
          </Button>
        ) : null}
        <Button
          type="button"
          disabled={!regionId || match.isPending}
          onClick={() => {
            save(regionId);
          }}
        >
          {match.isPending ? 'Saving…' : 'Save'}
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
}

/**
 * Adopt a discovered site: create its site controller (a UniFi controller in
 * the region it belongs to, polled by that region's gateways) or link one made
 * by hand for this console.
 */
export function AdoptSiteSheet({
  parent,
  site,
  onClose,
}: {
  parent: Controller;
  site: DiscoveredSite | null;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const organizationId = user?.organizationId;
  const options = usePlacementOptions(site);
  const { data: controllers } = useControllers(organizationId);
  const { data: gateways } = useGatewayStatus(organizationId);
  const adopt = useAdoptSite(parent.id);
  const createCredential = useCreateCredential(organizationId);

  const [regionId, setRegionId] = useState('');
  const [mode, setMode] = useState<'create' | 'link'>('create');
  const [linkId, setLinkId] = useState('');
  const [connection, setConnection] = useState<ConsoleConnection>('cloud');
  const [lanUrl, setLanUrl] = useState('');
  const [name, setName] = useState('');
  const [keyChoice, setKeyChoice] = useState(NEW_KEY);
  const [keyValue, setKeyValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const owner = options.find((o) => o.placement.regionId === regionId)?.placement.owner ?? '';

  // Controllers that could already be this console's: UniFi, not yet linked,
  // in the chosen region. One whose URL names the console's address
  // is almost certainly it.
  const linkable = useMemo(
    () =>
      (controllers ?? [])
        .filter(
          (c) =>
            c.type === 'unifi' && !c.discoveredSiteId && (!regionId || c.regionId === regionId),
        )
        .map((c) => ({
          controller: c,
          likely: !!site?.consoleAddress && c.baseUrl.includes(site.consoleAddress),
        }))
        .sort((a, b) => Number(b.likely) - Number(a.likely)),
    [controllers, regionId, site],
  );

  const regionHasGateway = (gateways ?? []).some(
    (g) => g.regionId === regionId && g.status === 'online',
  );

  // Seed the form when another site is opened; the options list refines as regions
  // load, but only a new site re-seeds. `undefined` to start, so a site passed at
  // mount seeds too.
  const [formFor, setFormFor] = useState<DiscoveredSite | null | undefined>(undefined);
  if (site !== formFor) {
    setFormFor(site);
    if (site) {
      const suggested = options.find((o) => o.suggested)?.placement.regionId ?? '';
      setRegionId(site.regionId ?? suggested);
      setMode('create');
      setLinkId('');
      setConnection('cloud');
      setLanUrl(site.consoleAddress ? `https://${hostForUrl(site.consoleAddress)}` : '');
      setKeyChoice(NEW_KEY);
      setKeyValue('');
      setError(null);
    }
  }

  // The suggested name follows the site and the owner of the chosen region.
  const [namedFor, setNamedFor] = useState<{ owner: string; site: DiscoveredSite | null } | undefined>(
    undefined,
  );
  if (namedFor?.owner !== owner || namedFor.site !== site) {
    setNamedFor({ owner, site });
    if (site) setName(owner ? `${owner} – ${siteLabel(site)}` : siteLabel(site));
  }

  if (mode === 'link' && !linkId && linkable[0]?.likely) setLinkId(linkable[0].controller.id);

  if (!site) return null;

  const baseUrl = connection === 'cloud' ? cloudConsoleUrl(site.externalConsoleId) : lanUrl.trim();
  const keyKind = connection === 'cloud' ? 'UniFi cloud key' : 'console key';

  const submit = async (): Promise<void> => {
    setError(null);
    if (mode === 'link') {
      if (!linkId) {
        setError('Choose the controller that already manages this console.');
        return;
      }
      adopt.mutate(
        { siteId: site.id, body: { controllerId: linkId } },
        {
          onSuccess: onClose,
          onError: (e) => {
            setError(apiErrorMessage(e, 'Could not link the controller.'));
          },
        },
      );
      return;
    }
    if (!regionId) {
      setError('Choose where this console belongs.');
      return;
    }
    if (!name.trim() || !baseUrl) {
      setError('A name and a URL are required.');
      return;
    }
    if (keyChoice === NEW_KEY && !keyValue.trim()) {
      setError('Paste the API key.');
      return;
    }
    setSaving(true);
    try {
      let credentialId = keyChoice;
      if (keyChoice === NEW_KEY) {
        // The key is sent once, to be relayed to that region's gateways; the
        // SaaS keeps only its name.
        const created = await createCredential.mutateAsync({
          name: `${owner || siteLabel(site)} – ${keyKind}`,
          type: 'apiKey',
          key: keyValue.trim(),
          regionId,
          description: `For ${siteLabel(site)}, adopted from ${parent.name}.`,
        });
        credentialId = created.id;
      }
      await adopt.mutateAsync({
        siteId: site.id,
        body: {
          regionId,
          name: name.trim(),
          baseUrl,
          credentialId,
          verifyTls: connection === 'cloud',
        },
      });
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not adopt this site.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Adopt ${siteLabel(site)}`}
      size="lg"
    >
      <AppSheet.Body>
        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="adopt-region">Belongs to</Label>
            <BelongsToSelect
              id="adopt-region"
              value={regionId}
              onChange={setRegionId}
              options={options}
            />
            {regionId && gateways && !regionHasGateway ? (
              <p className="text-xs text-sev-2">
                No gateway in this region is online yet. The site controller will wait until one
                connects.
              </p>
            ) : null}
          </div>

          <RadioGroup
            value={mode}
            onValueChange={(v) => {
              setMode(v as 'create' | 'link');
            }}
            className="gap-3"
          >
            <label className="flex items-start gap-2 text-sm">
              <RadioGroupItem value="create" className="mt-0.5" />
              <span>
                <span className="font-medium">Create a site controller</span>
                <span className="block text-xs text-muted-foreground">
                  A UniFi controller for this console, polled by the gateways where it belongs.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <RadioGroupItem value="link" className="mt-0.5" />
              <span>
                <span className="font-medium">Link a controller you already added</span>
                <span className="block text-xs text-muted-foreground">
                  Keeps it as it is and shows it as managed through Site Manager.
                </span>
              </span>
            </label>
          </RadioGroup>

          {mode === 'link' ? (
            <div className="space-y-1.5">
              <Label htmlFor="adopt-link">Controller</Label>
              <Select value={linkId} onValueChange={setLinkId}>
                <SelectTrigger id="adopt-link">
                  <SelectValue
                    placeholder={
                      linkable.length ? 'Choose a controller' : 'No UniFi controllers to link'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {linkable.map(({ controller, likely }) => (
                    <SelectItem key={controller.id} value={controller.id}>
                      {controller.name} — {controller.baseUrl}
                      {likely ? '  · matches this console' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>How should Witina reach this console?</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(
                    [
                      {
                        value: 'cloud' as const,
                        icon: Cloud,
                        title: "Through UniFi's cloud",
                        detail: 'Recommended. Needs a Site Manager key limited to this console.',
                      },
                      {
                        value: 'lan' as const,
                        icon: Network,
                        title: "Directly on the console's network",
                        detail: 'Needs a key created on the console. The gateway must reach it.',
                      },
                    ] satisfies {
                      value: ConsoleConnection;
                      icon: typeof Cloud;
                      title: string;
                      detail: string;
                    }[]
                  ).map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => {
                        setConnection(o.value);
                        setKeyChoice(NEW_KEY);
                      }}
                      className={cn(
                        'rounded-lg border p-3 text-left text-sm transition-colors',
                        connection === o.value ? 'border-primary bg-primary/5' : 'hover:bg-muted',
                      )}
                    >
                      <span className="flex items-center gap-2 font-medium">
                        <o.icon className="w-4 h-4" />
                        {o.title}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-1">{o.detail}</span>
                    </button>
                  ))}
                </div>
              </div>

              {connection === 'lan' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="adopt-url">Console URL</Label>
                  <Input
                    id="adopt-url"
                    value={lanUrl}
                    onChange={(e) => {
                      setLanUrl(e.target.value);
                    }}
                    placeholder="https://192.168.1.1"
                  />
                  <p className="text-xs text-muted-foreground">
                    The address the console reports for itself. Change it if the gateway reaches the
                    console another way.
                  </p>
                </div>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="adopt-key">API key</Label>
                <CredentialSelect
                  id="adopt-key"
                  value={keyChoice === NEW_KEY ? null : keyChoice}
                  onChange={(id) => { setKeyChoice(id ?? NEW_KEY); }}
                  regionId={regionId}
                  types={API_KEY_TYPES}
                  // A new key is made below, with steps for this console, and
                  // saved when the site is adopted.
                  ownNew={{
                    label: 'Paste a new key',
                    selected: keyChoice === NEW_KEY,
                    onSelect: () => { setKeyChoice(NEW_KEY); },
                  }}
                />
                {keyChoice === NEW_KEY ? (
                  <UniFiKeyPanel
                    id="adopt-key-value"
                    source={
                      connection === 'cloud'
                        ? { kind: 'cloud-site', site: siteLabel(site) }
                        : {
                            kind: 'console',
                            href: consoleIntegrationsUrl(site.externalConsoleId, site.siteReference),
                            hrefLabel: "this console's Integrations page",
                          }
                    }
                    value={keyValue}
                    onChange={setKeyValue}
                    savedAs={`${owner || siteLabel(site)} – ${keyKind}`}
                  />
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="adopt-name">Name</Label>
                <Input
                  id="adopt-name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Site{' '}
                  <Badge variant="secondary" className="font-normal">
                    {site.siteReference}
                  </Badge>{' '}
                  is set from Site Manager.
                </p>
              </div>
            </>
          )}
        </div>
      </AppSheet.Body>
      <AppSheet.Footer>
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={saving || adopt.isPending}
          onClick={() => {
            void submit();
          }}
        >
          {saving || adopt.isPending
            ? 'Adopting…'
            : mode === 'link'
              ? 'Link controller'
              : 'Adopt site'}
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
}
